import AdSenseBanner from "@/components/AdSenseBanner";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar, Play, MessageCircle, ShoppingBag, Zap, Users, Star, Trophy, MessageSquare, Layers, Eye, Heart
} from "lucide-react";
import { useEffect, useState } from "react";
import IPPill from "@/components/IPPill";
import StatCard from "@/components/StatCard";
import SectionHeader from "@/components/SectionHeader";
import ServerCard from "@/components/ServerCard";
import EventCard from "@/components/EventCard";
import LivePlayerGrid from "@/components/LivePlayerGrid";
import ActivityFeed from "@/components/ActivityFeed";
import PlayZoneLeaderboard from "@/components/PlayZoneLeaderboard";
import PostCard from "@/components/PostCard";
import { sfx } from "@/lib/sounds";
import {
  fetchServerStatus, fetchStats, fetchServers, fetchEvents, fetchBuilds, fetchLeaderboard, fetchDiscordLive, DISCORD_URL, fetchFeed,
} from "@/lib/api";
import AnimatedCounter from "@/components/AnimatedCounter";

const HERO_BG = "https://media.lostdrkc.in/wp-content/uploads/2026/09/adarfa-scaled.png";

// Helper to shuffle array for random builds display
function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function Home() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [servers, setServers] = useState([]);
  const [events, setEvents] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [topPvp, setTopPvp] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [discord, setDiscord] = useState(null);

  useEffect(() => {
    fetchServerStatus().then(setStatus).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
    fetchServers().then(setServers).catch(() => {});
    fetchDiscordLive().then(setDiscord).catch(() => {});
    fetchEvents().then(setEvents).catch(() => {});
    
    // Fetch builds and randomize
    fetchBuilds()
      .then((data) => {
        const buildsList = Array.isArray(data) ? data : (data.builds || []);
        setBuilds(shuffleArray(buildsList));
      })
      .catch(() => {});
    
    fetchLeaderboard("pvp")
      .then((d) => setTopPvp(d.entries || []))
      .catch(() => {});
      
    fetchFeed({ tab: "trending", limit: 3 })
      .then((d) => setFeedPosts(d.posts || []))
      .catch(() => {});

    const id = setInterval(() => {
      fetchServerStatus().then(setStatus).catch(() => {});
      fetchStats().then(setStats).catch(() => {});
      fetchDiscordLive().then(setDiscord).catch(() => {});
    }, 30000);

    return () => clearInterval(id);
  }, []);

  const featured = servers.filter((s) => s.is_featured);

  // Split builds into primary feature, side items, and bottom grid
  const featuredBuild = builds[0] || null;
  const sideBuilds = builds.slice(1, 3);
  const bottomBuilds = builds.slice(3, 7);

  const handleBuildClick = (buildId) => {
    sfx.click();
    navigate(`/builds?id=${buildId}`);
  };

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-20" data-testid="hero-section">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.2, 0.9, 0.3, 1] }}
          className="absolute inset-0 overflow-hidden"
        >
          <img
            src={HERO_BG}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-right md:object-center"
            style={{
              transform: "translateY(40px) scale(1.08)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 md:bg-gradient-to-l md:from-black md:via-black/90 md:via-black/60 md:via-35% transparent" />

          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />
        </motion.div>

        <div className="pointer-events-none absolute inset-0 hidden md:block">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="float-block absolute rounded-md border border-[#FFBF00]/30 shadow-[0_0_30px_rgba(255,191,0,0.15)]"
              style={{
                left: `${10 + i * 18}%`,
                top: `${15 + (i % 3) * 20}%`,
                width: 36 + (i % 2) * 20,
                height: 36 + (i % 2) * 20,
                background: `linear-gradient(135deg, rgba(255,191,0,${0.1 + i * 0.05}), rgba(255,140,0,${0.05 + i * 0.03}))`,
                transform: `rotate(${i * 12}deg)`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${6 + i}s`,
                // eslint-disable-next-line
                ["--rot"]: `${i * 12}deg`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 w-full flex justify-center md:justify-end px-6 md:pr-8 lg:pr-12">
          <div className="max-w-2xl w-full text-center md:text-right">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-[11px] uppercase tracking-[0.3em] mx-auto md:mx-0"
            >
              <span className={`w-2 h-2 rounded-full ${status?.online ? "bg-green-500 pulse-dot" : "bg-red-500"}`} />
              {status?.online ? "Network Online" : "Checking Network..."} · {status?.version || "1.20.x"}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-6 font-hero text-5xl sm:text-6xl md:text-8xl leading-[0.9] tracking-tight text-center md:text-right"
            >
              Welcome to <br className="hidden sm:block" />
              <span className="text-[#FFBF00] glow-text-yellow">LOSTDC</span> Network
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-5 text-base sm:text-lg md:text-xl text-white/70 max-w-2xl mx-auto md:ml-auto text-center md:text-right"
            >
              A free-to-play 24/7 Java & Bedrock Minecraft network packed with DC Coins, exciting events, unique game modes, and crazy adventures!
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-8 flex flex-wrap justify-center md:justify-end gap-3"
            >
              <button
                data-testid="hero-play-now"
                onClick={() => {
                  sfx.levelup();
                  navigator.clipboard.writeText("play.lostdrkc.in").catch(() => {});
                  window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFBF00] text-black font-bold uppercase tracking-wider hover:bg-[#FF8C00] transition-colors glow-yellow"
              >
                <Play size={16} fill="currentColor" /> Play Now
              </button>
              <a
                data-testid="hero-discord"
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => sfx.pling()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass hover:border-[#FFBF00]/40 font-semibold uppercase tracking-wider"
              >
                <MessageCircle size={16} /> Join Discord
              </a>
              <Link
                data-testid="hero-store"
                to="/store"
                onClick={() => sfx.click()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-[#FFBF00]/40 font-semibold uppercase tracking-wider"
              >
                <ShoppingBag size={16} /> Visit Store
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-10 flex flex-wrap justify-center md:justify-end gap-4"
            >
              <IPPill large />
              <div className="flex items-center gap-3 px-4 py-3 rounded-full glass">
                <Users size={16} className="text-[#FFBF00]" />
                <div className="font-mono text-sm">
                  <AnimatedCounter value={status?.players_online || 0} />
                  <span className="text-white/40"> / {status?.players_max || 500} online</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Smooth Bottom Fade Gradient */}
        <div className="absolute bottom-0 inset-x-0 h-48 bg-gradient-to-b from-transparent via-black/60 to-black pointer-events-none" />
      </section>

      {/* ============ STATS ============ */}
      <section
        className="relative py-24 px-4 sm:px-6"
        data-testid="stats-section"
      >
        <div className="max-w-7xl mx-auto">

          <SectionHeader
            eyebrow="Live Network"
            title="A Community That Never Sleeps"
            subtitle="Thousands of players. Countless memories. One community."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">

            {/* PLAYERS ONLINE */}
            <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] p-7 min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:border-[#FFBF00]/30">
              <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-[#FFBF00]/10 blur-3xl group-hover:bg-[#FFBF00]/20 transition-all" />

              <div className="relative flex flex-col h-full justify-between">

                <div className="flex items-center justify-between">
                  <div className="w-13 h-13 rounded-2xl bg-[#FFBF00]/10 border border-[#FFBF00]/20 flex items-center justify-center">
                    <Users size={23} className="text-[#FFBF00]" />
                  </div>

                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/30">
                    Minecraft
                  </span>
                </div>

                <div className="mt-8">
                  <div className="font-hero text-5xl sm:text-6xl leading-none text-white">
                    {(stats?.players_online || 0).toLocaleString("en-IN")}
                  </div>

                  <div className="mt-3 text-xs uppercase tracking-[0.22em] text-white/45">
                    Players Online
                  </div>
                </div>

              </div>
            </div>

            {/* SERVERS ONLINE */}
            <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] p-7 min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:border-[#FF8C00]/30">
              <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-[#FF8C00]/10 blur-3xl" />

              <div className="relative flex flex-col h-full justify-between">

                <div className="flex items-center justify-between">
                  <div className="w-13 h-13 rounded-2xl bg-[#FF8C00]/10 border border-[#FF8C00]/20 flex items-center justify-center">
                    <Zap size={23} className="text-[#FF8C00]" />
                  </div>

                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/30">
                    Network
                  </span>
                </div>

                <div className="mt-8">
                  <div className="font-hero text-5xl sm:text-6xl leading-none text-white">
                    {(stats?.servers_online || 0).toLocaleString("en-IN")}
                  </div>

                  <div className="mt-3 text-xs uppercase tracking-[0.22em] text-white/45">
                    Servers Online
                  </div>
                </div>

              </div>
            </div>

            {/* PLAYERS JOINED */}
            <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] p-7 min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:border-white/25">

              <div className="relative flex flex-col h-full justify-between">

                <div className="flex items-center justify-between">
                  <div className="w-13 h-13 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
                    <Star size={23} className="text-white/75" />
                  </div>

                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/30">
                    Community
                  </span>
                </div>

                <div className="mt-8">
                  <div className="font-hero text-5xl sm:text-6xl leading-none text-white">
                    {(stats?.total_registered || 0).toLocaleString("en-IN")}
                  </div>

                  <div className="mt-3 text-xs uppercase tracking-[0.22em] text-white/45">
                    Players Joined
                  </div>
                </div>

              </div>
            </div>

            {/* DISCORD MEMBERS — LIVE */}
            <div className="group relative overflow-hidden rounded-3xl border border-[#5865F2]/35 bg-[#5865F2]/[0.07] p-7 min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:border-[#5865F2]/70 hover:bg-[#5865F2]/[0.10]">

              <div className="absolute -right-16 -top-16 w-44 h-44 rounded-full bg-[#5865F2]/20 blur-3xl" />

              <div className="relative flex flex-col h-full justify-between">

                <div className="flex items-center justify-between">

                  <div className="w-13 h-13 rounded-2xl bg-[#5865F2]/15 border border-[#5865F2]/30 flex items-center justify-center">
                    <MessageCircle size={23} className="text-[#7289DA]" />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />

                    <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80">
                      Live
                    </span>
                  </div>

                </div>

                <div className="mt-8">

                  <div className="font-hero text-5xl sm:text-6xl leading-none text-white">
                    {discord?.guild?.members != null
                      ? discord.guild.members.toLocaleString("en-IN")
                      : "—"}
                  </div>

                  <div className="mt-3 text-xs uppercase tracking-[0.22em] text-white/55">
                    Discord Members
                  </div>

                  <div className="mt-2 text-[11px] text-white/30">
                    Live from {discord?.guild?.name || "Discord"}
                  </div>

                </div>

              </div>
            </div>

            {/* DISCORD ONLINE */}
            <div className="group relative overflow-hidden rounded-3xl border border-emerald-400/20 bg-emerald-400/[0.035] p-7 min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-400/40">

              <div className="relative flex flex-col h-full justify-between">

                <div className="flex items-center justify-between">

                  <div className="w-13 h-13 rounded-2xl bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center">
                    <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                  </div>

                  <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/60">
                    Discord
                  </span>

                </div>

                <div className="mt-8">

                  <div className="font-hero text-5xl sm:text-6xl leading-none text-white">
                    {discord?.guild?.online != null
                      ? discord.guild.online.toLocaleString("en-IN")
                      : "—"}
                  </div>

                  <div className="mt-3 text-xs uppercase tracking-[0.22em] text-white/45">
                    Discord Online
                  </div>

                </div>

              </div>
            </div>

            {/* EVENTS */}
            <div className="group relative overflow-hidden rounded-3xl border border-[#FFBF00]/20 bg-[#FFBF00]/[0.035] p-7 min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:border-[#FFBF00]/40">

              <div className="absolute -right-16 -top-16 w-44 h-44 rounded-full bg-[#FFBF00]/10 blur-3xl" />

              <div className="relative flex flex-col h-full justify-between">

                <div className="flex items-center justify-between">

                  <div className="w-13 h-13 rounded-2xl bg-[#FFBF00]/10 border border-[#FFBF00]/20 flex items-center justify-center">
                    <Calendar size={23} className="text-[#FFBF00]" />
                  </div>

                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/30">
                    Community
                  </span>

                </div>

                <div className="mt-8">

                  <div className="font-hero text-5xl sm:text-6xl leading-none text-white">
                    {Array.isArray(events)
                      ? events.length.toLocaleString("en-IN")
                      : "—"}
                  </div>

                  <div className="mt-3 text-xs uppercase tracking-[0.22em] text-white/45">
                    Events
                  </div>

                  <div className="mt-2 text-[11px] text-white/30">
                    Live from LostDC Events
                  </div>

                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ============ FEATURED SERVERS ============ */}
      {featured.length > 0 && (
        <section className="relative py-16 px-4 sm:px-6" data-testid="featured-servers-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Star size={18} className="text-[#FFBF00]" />
              <div className="text-[11px] uppercase tracking-[0.4em] text-[#FFBF00]">Featured</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featured.map((s, i) => <ServerCard key={s.id} server={s} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* ============ ALL SERVERS ============ */}
      <section className="relative py-16 px-4 sm:px-6" data-testid="servers-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Servers" title="Pick Your Battlefield" subtitle="Different unique worlds, one network. Choose your playstyle." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {servers.map((s, i) => <ServerCard key={s.id} server={s} index={i} />)}
          </div>
        </div>
      </section>

      {/* ============ LIVE PLAYERS ============ */}
      <section className="relative py-20" data-testid="players-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6">
          <SectionHeader eyebrow="Live · LosTDC NetWork" title="Players Online Now" subtitle="Aggregated live from every server through the LosTDC Network." />
        </div>
        <LivePlayerGrid />
      </section>

      {/* ============ BUILDS SHOWCASE ============ */}
      <section className="relative py-24 px-4 sm:px-6 bg-black/40" data-testid="builds-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <SectionHeader 
              eyebrow="Gallery" 
              title="Community Masterpieces" 
              subtitle="Explore architecture and creations built by our players across seasons." 
            />
            <Link 
              to="/builds" 
              onClick={() => sfx.click()} 
              data-testid="home-builds-cta" 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-bold transition-all hover:scale-105 shadow-lg shadow-[#FFBF00]/20"
            >
              Explore Full Gallery →
            </Link>
          </div>

          {builds.length > 0 ? (
            <div className="space-y-6">
              {/* TOP LAYOUT: BIG FEATURED BUILD (LEFT) + 2 STACKED BUILDS (RIGHT) */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* BIG FEATURED BUILD (2 COLUMNS) */}
                {featuredBuild && (
                  <div
                    onClick={() => handleBuildClick(featuredBuild.id)}
                    className="lg:col-span-2 group relative rounded-3xl overflow-hidden bg-black border border-[#FFBF00]/30 hover:border-[#FFBF00] transition-all duration-500 cursor-pointer shadow-2xl flex flex-col justify-end min-h-[380px] sm:min-h-[460px]"
                  >
                    <img
                      src={featuredBuild.image || (featuredBuild.images && featuredBuild.images[0])}
                      alt={featuredBuild.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    
                    <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full bg-[#FFBF00] text-black text-[10px] uppercase tracking-widest font-bold shadow-md">
                        Featured Build
                      </span>
                      <span className="px-3 py-1 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-white/80 text-[10px] uppercase tracking-widest font-mono">
                        {featuredBuild.category}
                      </span>
                    </div>

                    <div className="relative z-10 p-6 sm:p-8">
                      <h3 className="font-hero text-3xl sm:text-5xl text-white group-hover:text-[#FFBF00] transition-colors leading-tight">
                        {featuredBuild.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-white/70 mt-2 line-clamp-2 max-w-xl">
                        {featuredBuild.description || "An incredible build created by the community."}
                      </p>
                      
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10 text-xs text-white/80">
                        <span>by <strong className="text-white">{featuredBuild.author_name || featuredBuild.author}</strong></span>
                        <span className="flex items-center gap-1"><Heart size={13} className="text-[#FFBF00]" /> {featuredBuild.likes_count ?? 0}</span>
                        <span className="flex items-center gap-1"><Eye size={13} /> {featuredBuild.views ?? 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* SIDE BUILDS (1 COLUMN - 2 STACKED) */}
                <div className="flex flex-col gap-6">
                  {sideBuilds.map((b) => (
                    <div
                      key={b.id}
                      onClick={() => handleBuildClick(b.id)}
                      className="group relative flex-1 rounded-2xl overflow-hidden bg-black border border-white/10 hover:border-[#FFBF00]/60 transition-all duration-300 cursor-pointer flex flex-col justify-end min-h-[210px]"
                    >
                      <img
                        src={b.image || (b.images && b.images[0])}
                        alt={b.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                      
                      <div className="relative z-10 p-5">
                        <div className="text-[9px] uppercase tracking-widest text-[#FFBF00] font-semibold">{b.category}</div>
                        <h4 className="font-hero text-xl text-white group-hover:text-[#FFBF00] transition-colors mt-0.5 truncate">{b.title}</h4>
                        <div className="text-xs text-white/60 mt-1">by {b.author_name || b.author}</div>
                      </div>
                    </div>
                  ))}
                </div>

              </div>

              {/* BOTTOM BUILDS GRID */}
              {bottomBuilds.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pt-2">
                  {bottomBuilds.map((b) => (
                    <div
                      key={b.id}
                      onClick={() => handleBuildClick(b.id)}
                      className="group relative rounded-xl overflow-hidden bg-black border border-white/10 hover:border-[#FFBF00]/60 transition-all duration-300 cursor-pointer aspect-video flex flex-col justify-end"
                    >
                      <img
                        src={b.image || (b.images && b.images[0])}
                        alt={b.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                      
                      <div className="relative z-10 p-4">
                        <div className="text-[9px] uppercase tracking-widest text-[#FFBF00] font-semibold">{b.category}</div>
                        <h4 className="font-hero text-lg text-white group-hover:text-[#FFBF00] transition-colors truncate">{b.title}</h4>
                        <div className="text-[11px] text-white/60">by {b.author_name || b.author}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl glass p-12 text-center text-white/50">
              No builds found in gallery.
            </div>
          )}
        </div>
      </section>

      {/* ============ EVENTS ============ */}
      <section
        className="relative py-24 px-4 sm:px-6"
        data-testid="events-section"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Active Events"
            title="Compete. Build. Win."
            subtitle="Tournaments, festivals and secret hunts happening every week."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {events.slice(0, 4).map((e, i) => (
              <EventCard
                key={e.id}
                event={e}
                index={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ CREATORS ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="creators-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Creator Program"
            title="LostDC Creators"
            subtitle="Featured YouTubers, streamers and community stars — join or discover them here."
          />
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-2xl glass p-6 hover-lift flex flex-col justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Apply</div>
                <div className="font-hero text-3xl mt-2">Become a Creator</div>
                <p className="text-sm text-white/60 mt-2">Get a Creator badge, homepage feature, YouTube auto-sync and follower stats.</p>
              </div>
              <Link to="/creators/apply" onClick={() => sfx.pling()}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold self-start">
                Start Application
              </Link>
            </div>
            <div className="md:col-span-2 rounded-2xl glass p-6 hover-lift">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Featured Content</div>
              <div className="font-hero text-2xl mb-3">Latest Videos</div>
              <div className="text-sm text-white/60 mb-4">Videos tagged with <span className="font-mono text-[#FFBF00]">#lostdrkc #lostinlostdc #lostdcnetwork</span> auto-appear here.</div>
              <Link to="/creators" onClick={() => sfx.click()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-[#FFBF00]/50 text-xs uppercase tracking-widest">
                Open Creator Hub →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY FEED ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="community-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <SectionHeader
              eyebrow="Community Feed"
              title="Fresh from the Network"
              subtitle="Builds, clips, questions and hot takes from the LostDC community."
            />
            <Link to="/community" onClick={() => sfx.click()} data-testid="home-community-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold whitespace-nowrap">
              <MessageSquare size={14} /> Open Community
            </Link>
          </div>
          {feedPosts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-4">
              {feedPosts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          ) : (
            <div className="rounded-2xl glass p-10 text-center text-white/60">
              Be the first to post — head over to the <Link to="/community" className="text-[#FFBF00] hover:underline">Community feed</Link>.
            </div>
          )}
        </div>
      </section>

      {/* ============ ACTIVITY + LEADERBOARD SNIPPET ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="activity-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl glass p-6">
            <div className="flex items-center gap-2 mb-5">
              <Trophy size={18} className="text-[#FFBF00]" />
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">Top PvP · This Season</div>
            </div>
            <div className="space-y-2">
              {topPvp.slice(0, 5).map((e) => (
                <div key={e.rank} className="flex items-center gap-4 p-3 rounded-lg bg-black/40 border border-white/5">
                  <div className="font-hero text-2xl w-10 text-[#FFBF00]">#{e.rank}</div>
                  <img src={`https://mc-heads.net/avatar/${e.username}/64`} alt={e.username} className="w-9 h-9 rounded" loading="lazy" />
                  <div className="flex-1 font-semibold">{e.username}</div>
                  <div className="font-mono text-[#FFBF00]">{Number(e.value ?? 0).toLocaleString("en-IN")} <span className="text-white/40 text-xs">{e.unit}</span></div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Link to="/leaderboards" onClick={() => sfx.click()} className="text-xs uppercase tracking-widest text-[#FFBF00] hover:underline">View full leaderboards →</Link>
            </div>
          </div>
          <ActivityFeed />
        </div>
      </section>

      {/* ============ PLAYZONE LEADERBOARDS ============ */}
      <PlayZoneLeaderboard />

      {/* ============ ADSENSE BANNER ============ */}
      <AdSenseBanner />

      {/* ============ LIVE DISCORD ============ */}
      <section
        className="relative py-24 px-4 sm:px-6"
        data-testid="discord-section"
      >
        <div className="max-w-7xl mx-auto">

          <div className="grid lg:grid-cols-2 gap-8 items-stretch">

            {/* ================= LEFT — STATS + IMAGE ================= */}
            <div className="h-full flex flex-col">

              <SectionHeader
                eyebrow="Live Discord"
                title={discord?.guild?.name ? `Join ${discord.guild.name}` : "Join the Crew"}
                subtitle="Real-time community stats, announcements and updates directly from our Discord server."
              />

              {/* DISCORD STATS */}
              <div className="mt-8 grid grid-cols-2 gap-3">

                <div className="glass rounded-xl px-4 py-4">
                  <div className="font-hero text-3xl text-white">
                    {discord?.guild?.members != null
                      ? discord.guild.members.toLocaleString("en-IN")
                      : "—"}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-white/40">
                    Members
                  </div>
                </div>

                <div className="glass rounded-xl px-4 py-4">
                  <div className="font-hero text-3xl text-emerald-300">
                    {discord?.guild?.online != null
                      ? discord.guild.online.toLocaleString("en-IN")
                      : "—"}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-white/40">
                    Online Now
                  </div>
                </div>

                <div className="glass rounded-xl px-4 py-4">
                  <div className="font-hero text-3xl text-white">
                    {discord?.guild?.channels?.total != null
                      ? discord.guild.channels.total.toLocaleString("en-IN")
                      : "—"}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-white/40">
                    Channels
                  </div>
                </div>

                <div className="glass rounded-xl px-4 py-4">
                  <div className="font-hero text-3xl text-[#FFBF00]">
                    {discord?.guild?.boosts != null
                      ? discord.guild.boosts.toLocaleString("en-IN")
                      : "—"}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-white/40">
                    Boosts
                  </div>
                </div>

              </div>

              {/* ANNOUNCEMENT IMAGE — LEFT SIDE ONLY */}
              {(() => {
                const announcementImage =
                  discord?.announcement?.attachment?.url ||
                  discord?.announcement?.embeds?.find(
                    (e) => e?.image?.url
                  )?.image?.url ||
                  discord?.announcement?.embeds?.find(
                    (e) => e?.thumbnail?.url
                  )?.thumbnail?.url;

                if (!announcementImage) return null;

                return (
                  <div className="mt-5 rounded-2xl overflow-hidden glass border border-white/[0.08]">

                    <div className="px-5 py-3 border-b border-white/[0.06]">
                      <div className="text-[9px] uppercase tracking-[0.22em] text-white/35">
                        Announcement Media
                      </div>
                    </div>

                    <div className="bg-black/20 p-3">
                      <img
                        src={announcementImage}
                        alt="Discord announcement"
                        loading="lazy"
                        className="w-full max-h-[520px] object-contain rounded-xl"
                      />
                    </div>

                  </div>
                );
              })()}

            </div>


            {/* ================= RIGHT — ANNOUNCEMENT ================= */}
            <div className="relative h-full min-h-full rounded-2xl overflow-hidden glass p-6 sm:p-8 flex flex-col justify-between">

              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#5865F2]/30 blur-3xl" />

              {discord?.guild?.banner && (
                <img
                  src={discord.guild.banner}
                  alt=""
                  className="absolute inset-x-0 top-0 w-full h-28 object-cover opacity-20"
                />
              )}

              <div className="relative flex-1 flex flex-col">

                {discord?.announcement ? (
                  (() => {
                    const a = discord.announcement;

                    const lines = (a.content || "")
                      .split("\n")
                      .map((line) => line.trim())
                      .filter(Boolean);

                    const title =
                      lines[0] ||
                      "Latest Announcement";

                    const body =
                      lines.slice(1).join("\n") ||
                      "No announcement text available.";

                    return (
                      <div>

                        {/* AUTHOR */}
                        <div className="flex items-center gap-3">

                          {a.author?.avatar ? (
                            <img
                              src={a.author.avatar}
                              alt={a.author.username || "Discord"}
                              className="w-11 h-11 rounded-full ring-2 ring-[#5865F2]/30"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-[#5865F2]/20 flex items-center justify-center">
                              <MessageCircle
                                size={20}
                                className="text-[#7289DA]"
                              />
                            </div>
                          )}

                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white">
                                {a.author?.global_name ||
                                  a.author?.username ||
                                  "Discord"}
                              </span>

                              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#5865F2]/20 text-[#9AA7FF]">
                                Discord
                              </span>
                            </div>

                            {a.created_at && (
                              <div className="text-[10px] text-white/35 mt-0.5">
                                {new Date(a.created_at).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            )}
                          </div>

                        </div>


                        {/* MESSAGE */}
                        <div className="mt-7 flex-1 rounded-xl border-l-2 border-[#5865F2] bg-white/[0.025] px-5 py-6">

                          <div className="text-lg sm:text-xl font-bold text-white">
                            {title}
                          </div>

                          {body && (
                            <div className="mt-4 whitespace-pre-line text-sm sm:text-[15px] leading-7 text-white/65">
                              {body}
                            </div>
                          )}

                        </div>


                        {/* FOOTER */}
                        <div className="mt-6 pt-5 border-t border-white/[0.07] flex items-center justify-between gap-3">

                          <div className="text-[9px] uppercase tracking-[0.22em] text-white/25">
                            Live from Discord
                          </div>

                          {a.url && (
                            <a
                              href={a.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 text-xs font-semibold text-[#7289DA] hover:text-white transition-colors"
                            >
                              View on Discord
                              <span>↗</span>
                            </a>
                          )}

                        </div>

                      </div>
                    );
                  })()
                ) : (
                  <div className="py-16 text-center">

                    <MessageCircle
                      size={38}
                      className="mx-auto text-white/20"
                    />

                    <div className="mt-4 text-white/45">
                      Loading latest Discord announcement…
                    </div>

                  </div>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ============ BOTTOM ADSENSE BANNER ============ */}
      <AdSenseBanner />
    </div>
  );
}